import { ApplicationRef, DoBootstrap, inject, Inject, NgModule, provideAppInitializer } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { UrlService, UIRouterModule, UIRouter } from '@uirouter/angular';
import { catchError, delay, first, switchMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from 'rxjs';

import browserHelper from '@utils/browser-helper';
import locationHelper from '@utils/location-helper';
import { blankShield } from '@utils/blankshield';
import { featureFlags } from '@utils/feature-flags';
import { storage } from '@utils/storage.service';
import { IFBWindow } from './models/window-namespace';

import { globalErrorInterceptor } from './http-interceptors/global-error-interceptor';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { AuthModule } from './modules/auth/auth.module';
import { ConsentFormModule } from './modules/ui/consent-form/consent-form.module';
import { CustomFormModule } from './modules/ui/custom-form/custom-form.module';
import { DialogModule } from './modules/ui/dialog/dialog.module';
import { HeaderModule } from './modules/header/header.module';
import { JobShareModule } from './modules/job-share-routing-ft/job-share.module';
import { LegalTermsFooterModule } from './modules/ui/legal-terms-footer/legal-terms-footer.module';
import { LegalTermsModule } from './modules/legal-terms/legal-terms.module';
import { LoginModule } from './modules/login/login.module';
import { MobilePopoverModule } from './modules/ui/mobile-popover/mobile-popover.module';
import { ModalsModule } from './modules/ui/modals/modals.module';
import { NotFoundModule } from './modules/not-found/not-found.module';
import { PhoneInputModule } from './modules/ui/phone-input/phone-input.module';
import { ReferralModule } from './modules/referral/referral.module';
import { ResumeUploaderModule } from './modules/ui/resume-uploader/resume-uploader.module';
import { SharedModule } from './shared/shared.module';
import { TableOfContentsModule } from './modules/ui/table-of-contents/table-of-contents.module';

import { AppRunService } from './app-run';
import { CollectionModule } from './modules/ui/collection/collection.module';
import { ColorThemeResource } from './shared/modules/resources/color-theme.resource';
import { ColorThemeService } from './shared/services/color-theme/color-theme.service';
import { CompanyResource } from './shared/modules/resources/company.resource';
import { CompanyService } from './shared/services/company.service';
import { FeatureTogglesResource } from './shared/modules/resources/feature-toggles.resouce';
import { LanguagesService } from './shared/services/languages.service';
import { logger } from '@utils/logger.service';
import { PageTitleService } from './shared/services/page-title.service';
import { SsoService } from './shared/services/sso.service';
import { UserManager } from './shared/services/user-manager.service';
import { AccountResource } from '@app/shared/modules/resources/account.resource';

import { AppPublicComponent } from './app-public.component';
import { AppInitializedEmitterService } from './shared/services/app-initialized-emitter.service';
import { AuthService } from './shared/services/auth.service';

@NgModule({
  imports: [
    AuthModule,
    BrowserAnimationsModule,
    BrowserModule,
    CollectionModule,
    ConsentFormModule,
    CustomFormModule,
    DialogModule,
    HeaderModule,
    JobShareModule,
    LegalTermsFooterModule,
    LegalTermsModule,
    LoginModule,
    MobilePopoverModule,
    ModalsModule,
    NotFoundModule,
    PhoneInputModule,
    ReferralModule,
    ResumeUploaderModule,
    SharedModule,
    TableOfContentsModule,
    UIRouterModule.forRoot({
      otherwise: { state: 'not-found' },
      deferIntercept: true
    })
  ],
  providers: [
    { provide: 'APP', useValue: 'public' },
    provideAppInitializer(initializePublicApp),
    provideHttpClient(withInterceptors([globalErrorInterceptor])),
    AppRunService
  ],
  declarations: [AppPublicComponent]
})
export class AppPublicModule implements DoBootstrap {
  private appInitializedEmitterSrv = inject(AppInitializedEmitterService);
  private router = inject(UIRouter);

  constructor(
    @Inject('Window') private window: IFBWindow,
    private appRunService: AppRunService,
    private pageTitle: PageTitleService,
    private urlService: UrlService,
    private authService: AuthService
  ) {
    this.setUrlProviderRules();
    this.appRunService.onInit();
    this.appInitializedEmitterSrv
      .isAppInitialized()
      .pipe(first(), delay(0))
      .subscribe(() => this.syncRouting());
  }

  ngDoBootstrap(appRef: ApplicationRef) {
    this.pageTitle.init();
    blankShield.init();
    this.appRunService.onBootstrap();
    this.appInitializedEmitterSrv.markAppAsInitialized();
    appRef.bootstrap(AppPublicComponent);
  }

  private syncRouting() {
    this.router.urlService.listen();
    this.router.urlService.sync();
  }

  private setUrlProviderRules() {
    this.urlService.rules.when('/', () => {
      // forces page reload for backend served login page
      const subdomain = browserHelper.subdomain();

      if (subdomain) {
        const search = this.window.location.search ? '&' + this.window.location.search.substring(1) : '';
        // Attempt to restore auth session
        this.authService.LOGIN_LINK.subscribe((loginLink: string) => {
          this.window.location.href = `${loginLink}?prompt=none${search}`;
        });
      } else {
        // Visiting a general website without a company, redirect to subdomain enter screen
        this.window.location.href = '/login';
      }
    });
  }
}

function initializePublicApp() {
  const accountResource = inject(AccountResource);
  const colorThemeResource = inject(ColorThemeResource);
  const colorThemeService = inject(ColorThemeService);
  const companyResource = inject(CompanyResource);
  const companyService = inject(CompanyService);

  const featureTogglesResource = inject(FeatureTogglesResource);
  const languagesService = inject(LanguagesService);
  const ssoService = inject(SsoService);
  const userManager = inject(UserManager);
  const window = inject(IFBWindow);

  const fetchData = () => {
    const token = storage.getItem('token') || locationHelper.hashParam('access_token');
    const requests: { [key: string]: Observable<any> } = {};

    // Get company info
    const subdomain = companyService.subdomain;
    if (subdomain) {
      requests.company = companyResource.getCompany(subdomain).pipe(
        tap(company => {
          // Setting company manager to have company id defined inside resources, e.g. tenant
          // This value won't propagate inside the app itself, available only inside invoke
          companyService.set(company);
        })
      );
    } else {
      requests.languages = languagesService.getAllowed().pipe(
        tap(languages => companyService.setEnabledLocales(languages.map(language => language.locale))),
        catchError(err => {
          logger.error(err);
          return of(null);
        })
      );
    }

    // Get user info
    if (token) {
      requests.account = accountResource.get(token).pipe(
        tap(account => userManager.set(account)),
        catchError(err => {
          logger.error(err);
          return of(null);
        })
      );
    }

    requests.features = featureTogglesResource.get().pipe(
      tap(response => featureFlags.set(response)),
      catchError(err => {
        logger.error(err);
        return of(null);
      })
    );

    return forkJoin(requests).pipe(
      // if any of the inner observables supplied to forkJoin error we will lose the value of
      //  any other observables that would or have already completed, that's why we add catch to each request
      catchError(err => {
        // If login fails with a company set, need to clear token
        if (token) {
          storage.removeItem('token');
        }
        logger.error(err);
        return of(null);
      }),
      switchMap(() => {
        if (companyService.data?.id) {
          // This requests can only be done once you know the companyId
          const companyRequests: { [key: string]: Observable<any> } = {};

          companyRequests.colorTheme = colorThemeResource.get().pipe(
            tap(theme => colorThemeService.applyTheme(theme)),
            catchError(err => {
              colorThemeService.applyTheme();
              logger.error(err);
              return of(null);
            })
          );

          companyRequests.companyFeatures = companyResource.getCompanyFeaturesConf().pipe(
            tap(companyFeatures => featureFlags.set(companyFeatures)),
            catchError(err => {
              logger.error(err);
              return of(null);
            })
          );

          companyRequests.languages = companyResource.getCompanyLanguages().pipe(
            tap(languages => companyService.setLanguages(languages)),
            catchError(err => {
              logger.error(err);
              return of(null);
            })
          );

          companyRequests.metadata = companyResource.getMetadataConf().pipe(
            tap(metadata => featureFlags.set(metadata.components)),
            catchError(err => {
              logger.error(err);
              return of(null);
            })
          );

          companyRequests.ssoOnly = ssoService.fetchIdentityProviders().pipe(
            catchError(err => {
              logger.error(err);
              return of(null);
            })
          );

          // if any of the inner observables supplied to forkJoin error we will lose the value of
          //  any other observables that would or have already completed, that's why we add catch to each request
          return forkJoin(companyRequests);
        } else {
          colorThemeService.init();
        }

        return of(null);
      })
    );
  };

  if (browserHelper.isCname()) {
    return companyResource.getMetadata().pipe(
      switchMap(metaData => {
        window.localStorage.setItem('companySubdomain', metaData.domainName);
        return fetchData();
      })
    );
  } else {
    return fetchData();
  }
}
